import { useEffect, useMemo, useState } from "react";
import { useForm } from "@tanstack/react-form";
import { Pencil, Trash2, UserPlus } from "lucide-react";

import { useTranslations } from "@/i18n";
import { usePlayer, usePlayerList } from "@/store/selectors";
import { useStore } from "@/store/store";
import type { Player } from "@/types";
import type { PlayerId } from "@/types/branded";
import {
  CardHeader,
  CardTitle,
  ConfirmDeleteRow,
  ConfirmDeleteText,
  CountBadge,
  ErrorText,
  FloodEmptyState,
  GlassCard,
  GlassInput,
  GlassRow,
  ListGap,
  PillBtn,
  PillBtnPrimary,
  RosterNumber,
  RowActions,
  RowName,
  SmallIconBtn,
} from "@/ui/styled";
import { theme } from "@/ui/theme";

const isDuplicateName = (players: Player[], name: string, excludeId?: PlayerId): boolean => {
  const lower = name.trim().toLowerCase();
  return players.some(
    (p) => p.name.toLowerCase() === lower && (excludeId == null || p.id !== excludeId),
  );
};

const PlayerItem = ({ playerId, index }: { playerId: PlayerId; index: number }) => {
  const renamePlayerAction = useStore((s) => s.renamePlayer);
  const deletePlayerAction = useStore((s) => s.deletePlayer);
  const players = usePlayerList();
  const player = usePlayer(playerId);

  const { t } = useTranslations();

  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Auto-reset confirm state
  useEffect(() => {
    if (!confirmDelete) return;
    const timer = setTimeout(() => setConfirmDelete(false), 2500);
    return () => clearTimeout(timer);
  }, [confirmDelete]);

  const defaultValues = useMemo(() => ({ name: player?.name ?? "" }), [player?.name]);

  const myForm = useForm({
    defaultValues,
    onSubmit: ({ value }) => {
      const trimmed = value.name.trim();
      if (trimmed && trimmed !== player?.name) renamePlayerAction(playerId, trimmed);
      setEditing(false);
    },
  });

  if (!player) return null;

  const handleDelete = () => {
    if (confirmDelete) {
      deletePlayerAction(player.id);
    } else {
      setConfirmDelete(true);
    }
  };

  // ── Confirm delete state ───────────────────────────────────
  if (confirmDelete) {
    return (
      <ConfirmDeleteRow>
        <RosterNumber>{index}</RosterNumber>
        <ConfirmDeleteText>{t("player.deleteConfirm", player.name)}</ConfirmDeleteText>
        <RowActions>
          <PillBtn
            type="button"
            onClick={() => setConfirmDelete(false)}
            style={{ fontSize: "0.75rem", padding: "0.25rem 0.625rem" }}
          >
            {t("activity.cancel")}
          </PillBtn>
          <SmallIconBtn style={{ color: theme.colors.danger }} onClick={handleDelete}>
            <Trash2 size={13} />
          </SmallIconBtn>
        </RowActions>
      </ConfirmDeleteRow>
    );
  }

  return (
    <GlassRow>
      <RosterNumber>{index}</RosterNumber>

      {editing ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            myForm.handleSubmit();
          }}
          style={{ flex: 1 }}
        >
          <myForm.Field
            name="name"
            validators={{
              onSubmit: ({ value }) => {
                const trimmed = value.trim();

                if (!trimmed) return t("general.required");

                if (isDuplicateName(players, trimmed, playerId))
                  return t("general.alreadyExists", trimmed);

                return undefined;
              },
            }}
          >
            {(field) => (
              <GlassInput
                type="text"
                placeholder={t("player.addPlaceholder")}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={() => myForm.handleSubmit()}
                autoFocus
                autoComplete="off"
              />
            )}
          </myForm.Field>
        </form>
      ) : (
        <RowName onDoubleClick={() => setEditing(true)}>{player.name}</RowName>
      )}

      <RowActions>
        <SmallIconBtn
          onClick={() => {
            myForm.reset();
            setEditing(true);
          }}
        >
          <Pencil size={13} />
        </SmallIconBtn>
        <SmallIconBtn style={{ color: theme.colors.danger }} onClick={handleDelete}>
          <Trash2 size={13} />
        </SmallIconBtn>
      </RowActions>
    </GlassRow>
  );
};

export default function PlayerList() {
  const playersById = useStore((s) => s.players);
  const addPlayerAction = useStore((s) => s.addPlayer);

  const { t } = useTranslations();

  const players = useMemo(() => Object.values(playersById), [playersById]);

  const form = useForm({
    defaultValues: { name: "" },

    onSubmit: ({ value }) => {
      const trimmed = value.name.trim();
      if (!trimmed) return;
      addPlayerAction(trimmed);
      form.reset();
    },
  });

  return (
    <GlassCard>
      <CardHeader>
        <CardTitle>
          {t("player.title")}
        </CardTitle>
        {players.length > 0 && <CountBadge>{players.length}</CountBadge>}
      </CardHeader>

      {players.length === 0 ? (
        <FloodEmptyState>{t("match.needPlayers")}</FloodEmptyState>
      ) : (
        <ListGap>
          {players.map((p, i) => (
            <PlayerItem key={p.id} playerId={p.id} index={i + 1} />
          ))}
        </ListGap>
      )}

      {/* Add form at bottom — "sign a new player" */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
        style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}
      >
        <form.Field
          name="name"
          validators={{
            onSubmit: ({ value }) => {
              const trimmed = value.trim();

              if (!trimmed) return t("general.required");

              if (isDuplicateName(players, trimmed)) return t("general.alreadyExists", trimmed);

              return undefined;
            },
          }}
        >
          {(field) => (
            <GlassInput
              type="text"
              placeholder={t("player.addPlaceholder")}
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              autoComplete="off"
            />
          )}
        </form.Field>
        <form.Subscribe selector={(s) => s.values.name}>
          {(name) => (
            <PillBtnPrimary type="submit" disabled={!name.trim()} style={{ flexShrink: 0 }}>
              <UserPlus size={14} />
            </PillBtnPrimary>
          )}
        </form.Subscribe>
      </form>

      <form.Subscribe selector={(s) => s.fieldMeta.name?.errors}>
        {(errors) =>
          errors?.length ? (
            <ErrorText style={{ marginTop: "0.375rem" }}>{errors.join(", ")}</ErrorText>
          ) : null
        }
      </form.Subscribe>
    </GlassCard>
  );
}
